import { useEventListener } from '@shared/Frontend/hooks/useEventListener'
import { ReactNode, useRef, useState } from 'react'
import { cn } from '../utils/addToClassName'
import { slugify } from '../utils/slugify'
import { InternalOrExternalLink } from './link'
import { SVGIcon } from './svg'
import React = require('react')

export type NavItem = { title: string; url?: string; isExternal?: boolean; isActive?: boolean }
export type ParentNavItem = NavItem & { subNav?: Array<NavItem> }

export const NavLayout = ({
  children,
  ariaLabel,
  id,
  navClassName,
  navListClassName
}: {
  children: ReactNode
  navClassName?: string
  navListClassName?: string
  id?: string
  ariaLabel?: string
}) => (
  <nav id={id} className={cn('nav', navClassName)} aria-label={ariaLabel || 'Primary'}>
    <ul className={cn('nav__list', navListClassName)}>{children}</ul>
  </nav>
)

export const NavItemLayout = ({
  title,
  isActive,
  isExternal,
  linkClassName,
  itemClassName,
  onClick,
  subNav,
  url
}: {
  itemClassName?: string
  linkClassName?: string
  subNav?: Array<NavItem>
  onClick?: () => void
} & NavItem) => {
  const menuItem = useRef<HTMLLIElement>(null)

  const hasDropdown = subNav && subNav.length > 0

  const [subIsOpen, setSubOpen] = useState(false)

  // Close dropdown on Escape
  useEventListener(
    'keydown',
    (e): void => {
      if (e.key === 'Escape') setSubOpen(false)
    },
    { passive: true },
    subIsOpen
  )

  return (
    <li
      ref={menuItem}
      className={cn(
        'nav__item',
        hasDropdown ? 'nav__item--dropdown' : '',
        hasDropdown && url ? 'nav__item--dropdown-has-link' : '',
        itemClassName
      )}
      onBlur={() => {
        if (subNav && subIsOpen && !menuItem.current?.matches(':focus-within')) setSubOpen(false)
      }}
    >
      {url && (
        <InternalOrExternalLink
          aria-current={isActive ? 'page' : undefined}
          className={cn('nav__link', linkClassName)}
          isExternal={isExternal || false}
          onClick={onClick}
          url={url || ''}
        >
          <span>{title}</span>
        </InternalOrExternalLink>
      )}

      {/* Dropdown */}
      {hasDropdown && subNav && (
        <NavItemDropdownLayout
          title={title}
          items={subNav}
          open={subIsOpen}
          hasURL={!!url}
          onToggle={() => setSubOpen((prev) => !prev)}
          onClick={() => {
            setSubOpen(false)
            if (onClick) onClick()
          }}
        />
      )}
    </li>
  )
}

const NavItemDropdownLayout = ({
  hasURL,
  items,
  open,
  title,
  onToggle,
  onClick
}: {
  title: string
  hasURL: boolean
  onToggle: () => void
  onClick?: () => void
  items: Array<NavItem>
  open: boolean
}) => {
  const id = `a11y-dropdown-${slugify(title)}`
  const label = hasURL ? `${open ? 'Sluit' : 'Open'} ${title}` : title
  return (
    <>
      <button
        className={cn('nav__button', !hasURL ? 'nav__link' : '')}
        onClick={onToggle}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={`a11y-nav-dropdown-${id}`}
        title={label}
      >
        <span className={hasURL ? 'sr-text' : ''}>{label}</span>
        <SVGIcon id="chevron-down" className="nav__chevron" />
      </button>

      <div id={`a11y-nav-dropdown-${id}`} hidden={!open} className="nav__dropdown">
        <div className="nav__dropdown-inner">
          <ul className="nav__dropdown-list">
            {items.map(
              (child) =>
                child.url && (
                  <li key={child.title} className="nav__dropdown-item">
                    <InternalOrExternalLink
                      url={child.url}
                      isExternal={child.isExternal || false}
                      className="nav__dropdown-link"
                      aria-current={child.isActive ? 'page' : undefined}
                      onClick={onClick}
                    >
                      <span>{child.title}</span>
                    </InternalOrExternalLink>
                  </li>
                )
            )}
          </ul>
        </div>
      </div>
    </>
  )
}
